import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus, X, CheckCircle2, PauseCircle, XCircle,
  Pencil, Ban, Play, Trash2, AlertTriangle, Globe, Loader2,
  Calendar, Clock, ClipboardList, ChevronLeft, ChevronRight, Download, Search, Mail, Archive,
} from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';
import * as Tabs from '@radix-ui/react-tabs';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { CicloVidaClinicaModal } from './CicloVidaClinicaModal';
import { getErrorMessage } from '@/lib/errors';
import { TIPOS_CLINICA } from '@/lib/perfilClinicaPresets';

/* ─ Tipos ─────────────────────────────────────────────────────────────────── */
type EstadoClinica = 'activa' | 'suspendida' | 'eliminada';

const TIPO_OPTS = TIPOS_CLINICA;

const ESTADO_BADGE: Record<EstadoClinica, { label: string; cls: string; icon: typeof CheckCircle2 }> = {
  activa:     { label: 'Activa',     cls: 'bg-emerald-900 text-emerald-300 border-emerald-700', icon: CheckCircle2 },
  suspendida: { label: 'Suspendida', cls: 'bg-amber-900 text-amber-300 border-amber-700',       icon: PauseCircle  },
  eliminada:  { label: 'Eliminada',  cls: 'bg-red-900 text-red-400 border-red-700',             icon: XCircle      },
};

const ACCION_LABEL: Record<string, string> = {
  crear: 'Creó', actualizar: 'Actualizó', eliminar: 'Eliminó',
  consultar: 'Consultó', login: 'Inició sesión', firmar: 'Firmó', exportar: 'Exportó',
};

const TAB_CLS = [
  'px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap',
  'text-slate-400 border-transparent hover:text-slate-200',
  'data-[state=active]:text-amber-400 data-[state=active]:border-amber-400',
].join(' ');

function fmtDate(v: string | null | undefined, includeTime = false) {
  if (!v) return '—';
  const d = new Date(v);
  const date = d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
  if (!includeTime) return date;
  return `${date} ${d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}`;
}

function neejBadge(neejStatus: any) {
  if (!neejStatus) return <span className="text-slate-600 text-xs">Sin código</span>;
  const ok = neejStatus.valid;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${
      ok ? 'bg-emerald-900 text-emerald-300 border-emerald-700' : 'bg-red-900 text-red-400 border-red-700'
    }`}>
      {neejStatus.status ?? (ok ? 'ACTIVA' : 'INVÁLIDA')}
    </span>
  );
}

const empty = {
  nombre: '', tipoClinica: 'medica_general', telefono: '', correo: '',
  sitioWeb: '', direccion: '', razonSocial: '', rfc: '', ciudad: '', estado: '',
  codigoNeej: '', emailAdmin: '', nombreAdmin: '',
};

const inputCls = 'w-full px-3 py-2.5 rounded-lg border border-slate-700 bg-slate-800 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500';
const labelCls = 'block text-xs font-medium text-slate-400 mb-1';

/* ─ Modal actividades ───────────────────────────────────────────────────────── */
function ActividadesModal({ clinica, onClose }: { clinica: any; onClose: () => void }) {
  const [page, setPage]         = useState(1);
  const [downloading, setDownloading] = useState(false);

  async function handleExport() {
    setDownloading(true);
    try {
      const res = await fetch(
        `${import.meta.env.VITE_API_URL ?? ''}/api/super-admin/clinicas/${clinica.id}/actividades/export`,
        { credentials: 'include' },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message ?? 'No se pudo descargar el archivo');
      }
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `actividades-${clinica.nombre.replace(/\s+/g, '-').toLowerCase()}-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      toast.error(err?.message ?? 'No se pudo descargar el archivo');
    } finally {
      setDownloading(false);
    }
  }

  const { data, isLoading } = useQuery({
    queryKey: ['sa-actividades', clinica.id, page],
    queryFn: () => api.get(`/api/super-admin/clinicas/${clinica.id}/actividades?page=${page}&limit=30`).then(r => r.data),
    staleTime: 0,
  });

  const items: any[] = data?.data ?? [];
  const pages: number = data?.pages ?? 1;

  return (
    <Dialog.Root open onOpenChange={v => { if (!v) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40" />
        <Dialog.Content className="fixed z-50 inset-x-0 bottom-0 md:inset-x-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:bottom-auto w-full md:max-w-3xl max-h-[90vh] flex flex-col bg-slate-900 rounded-t-3xl md:rounded-2xl border border-slate-700 shadow-2xl">
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700 shrink-0">
            <div>
              <Dialog.Title className="text-base font-bold text-white flex items-center gap-2">
                <ClipboardList size={16} className="text-amber-400" />
                Log de actividades — {clinica.nombre}
              </Dialog.Title>
              <p className="text-xs text-slate-500 mt-0.5">{data?.total ?? '…'} registros en total</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleExport}
                disabled={downloading}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-amber-400 border border-amber-800 hover:bg-amber-900/30 disabled:opacity-50 transition-colors"
              >
                {downloading ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
                Descargar CSV
              </button>
              <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
                <X size={18} />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="flex justify-center py-12"><Loader2 className="animate-spin text-slate-500" size={24} /></div>
            ) : items.length === 0 ? (
              <p className="text-center text-slate-600 py-12">Sin actividad registrada</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-800 sticky top-0">
                  <tr>
                    {['Fecha', 'Usuario', 'Acción', 'Recurso', 'IP'].map(h => (
                      <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {items.map(a => (
                    <tr key={a.id} className="hover:bg-slate-800/50 transition-colors">
                      <td className="px-4 py-2.5 text-xs text-slate-400 whitespace-nowrap">{fmtDate(a.fecha, true)}</td>
                      <td className="px-4 py-2.5">
                        <p className="text-xs text-white">{a.usuarioNombre ?? '—'}</p>
                        {a.usuarioEmail && <p className="text-xs text-slate-500">{a.usuarioEmail}</p>}
                      </td>
                      <td className="px-4 py-2.5">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                          a.accion === 'crear' ? 'bg-emerald-900 text-emerald-300' :
                          a.accion === 'eliminar' ? 'bg-red-900 text-red-400' :
                          a.accion === 'actualizar' ? 'bg-blue-900 text-blue-300' :
                          'bg-slate-800 text-slate-400'
                        }`}>
                          {ACCION_LABEL[a.accion] ?? a.accion}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-xs text-slate-400 max-w-[200px] truncate" title={a.entidad}>{a.entidad}</td>
                      <td className="px-4 py-2.5 text-xs text-slate-500">{a.ip ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {pages > 1 && (
            <div className="flex items-center justify-between px-6 py-3 border-t border-slate-700 shrink-0">
              <p className="text-xs text-slate-500">Página {page} de {pages}</p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 disabled:opacity-30 transition-colors"
                >
                  <ChevronLeft size={16} />
                </button>
                <button
                  onClick={() => setPage(p => Math.min(pages, p + 1))}
                  disabled={page === pages}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 disabled:opacity-30 transition-colors"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/* ─ Tabla por pestaña ───────────────────────────────────────────────────────── */
function ClinicasTabla({
  clinicas,
  onEdit,
  onLog,
  onEstado,
  onEliminar,
  onReenviar,
  onCicloVida,
  isPending,
}: {
  clinicas: any[];
  onEdit: (c: any) => void;
  onLog: (c: any) => void;
  onEstado: (id: string, estado: EstadoClinica) => void;
  onEliminar: (c: any) => void;
  onReenviar: (id: string) => void;
  onCicloVida: (c: any) => void;
  isPending: boolean;
}) {
  const [search, setSearch] = useState('');

  const filtered = clinicas.filter(c =>
    c.nombre?.toLowerCase().includes(search.toLowerCase()) ||
    c.ciudad?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4 pt-4">
      <div className="relative w-full max-w-sm">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <input
          placeholder="Buscar por nombre o ciudad…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-slate-700 bg-slate-900 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500"
        />
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16 text-slate-600">
          {search ? 'No hay coincidencias' : 'Sin clínicas en esta categoría'}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-slate-800">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-slate-900 border-b border-slate-800">
              <tr>
                {['Clínica', 'Registrada', 'Estado desde', 'NEEJ', 'Acciones'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {filtered.map(c => (
                <tr key={c.id} className="bg-slate-950 hover:bg-slate-900 transition-colors">
                  <td className="px-4 py-3">
                    <p className="font-medium text-white">{c.nombre}</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {c.tipoClinica?.replace(/_/g, ' ')} {c.ciudad ? `· ${c.ciudad}` : ''}
                    </p>
                  </td>
                  <td className="px-4 py-3">
                    <span className="flex items-center gap-1.5 text-xs text-slate-400">
                      <Calendar size={12} className="text-slate-600" />
                      {fmtDate(c.createdAt)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="flex items-center gap-1.5 text-xs text-slate-400">
                      <Clock size={12} className="text-slate-600" />
                      {fmtDate(c.estadoClinicaCambiadoEl)}
                    </span>
                  </td>
                  <td className="px-4 py-3">{neejBadge(c.neejStatus)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button onClick={() => onEdit(c)} title="Editar" className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors">
                        <Pencil size={14} />
                      </button>
                      <button onClick={() => onLog(c)} title="Ver log de actividades" className="p-1.5 rounded-lg text-slate-400 hover:text-amber-400 hover:bg-slate-800 transition-colors">
                        <ClipboardList size={14} />
                      </button>
                      <button onClick={() => onCicloVida(c)} title="Ciclo de vida / Retención" className="p-1.5 rounded-lg text-slate-400 hover:text-orange-400 hover:bg-slate-800 transition-colors">
                        <Archive size={14} />
                      </button>
                      {c.invitacionPendiente && (
                        <button
                          onClick={() => onReenviar(c.id)}
                          title="Reenviar invitación al administrador"
                          className="p-1.5 rounded-lg text-slate-400 hover:text-blue-400 hover:bg-slate-800 transition-colors"
                        >
                          <Mail size={14} />
                        </button>
                      )}
                      {c.estadoClinica === 'activa' && (
                        <button
                          onClick={() => onEstado(c.id, 'suspendida')}
                          disabled={isPending}
                          title="Suspender"
                          className="p-1.5 rounded-lg text-slate-400 hover:text-amber-400 hover:bg-slate-800 transition-colors disabled:opacity-50"
                        >
                          <Ban size={14} />
                        </button>
                      )}
                      {c.estadoClinica === 'suspendida' && (
                        <button
                          onClick={() => onEstado(c.id, 'activa')}
                          disabled={isPending}
                          title="Reactivar"
                          className="p-1.5 rounded-lg text-slate-400 hover:text-emerald-400 hover:bg-slate-800 transition-colors disabled:opacity-50"
                        >
                          <Play size={14} />
                        </button>
                      )}
                      {c.estadoClinica !== 'eliminada' && (
                        <button
                          onClick={() => onEliminar(c)}
                          title="Eliminar"
                          className="p-1.5 rounded-lg text-slate-400 hover:text-red-400 hover:bg-slate-800 transition-colors"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ─ Componente principal ────────────────────────────────────────────────────── */
export function SuperAdminClinicasPage() {
  const qc = useQueryClient();
  const [open, setOpen]               = useState(false);
  const [editing, setEditing]         = useState<any | null>(null);
  const [form, setForm]               = useState({ ...empty });
  const [confirmDelete, setConfirmDelete] = useState<any | null>(null);
  const [logClinica, setLogClinica]   = useState<any | null>(null);
  const [cicloVidaClinica, setCicloVidaClinica] = useState<any | null>(null);

  const { data: clinicas = [], isLoading } = useQuery<any[]>({
    queryKey: ['sa-clinicas'],
    queryFn: () => api.get('/api/super-admin/clinicas').then(r => r.data),
    staleTime: 0,
  });

  const createMut = useMutation({
    mutationFn: (d: any) => api.post('/api/super-admin/clinicas', d).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sa-clinicas'] });
      qc.invalidateQueries({ queryKey: ['sa-stats'] });
      closeDialog();
      toast.success('Clínica registrada — se envió la invitación al administrador');
    },
    onError: (e: any) => toast.error(getErrorMessage(e, 'Error al crear la clínica')),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, d }: { id: string; d: any }) => api.patch(`/api/super-admin/clinicas/${id}`, d).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sa-clinicas'] });
      closeDialog();
      toast.success('Clínica actualizada');
    },
    onError: (e: any) => toast.error(getErrorMessage(e, 'Error al actualizar')),
  });

  const reenviarMut = useMutation({
    mutationFn: (id: string) => api.post(`/api/super-admin/clinicas/${id}/reenviar-invitacion`).then(r => r.data),
    onSuccess: (data) => toast.success(`Invitación reenviada a ${data.email}`),
    onError: (e: any) => toast.error(getErrorMessage(e, 'Error al reenviar')),
  });

  const estadoMut = useMutation({
    mutationFn: ({ id, estadoClinica }: { id: string; estadoClinica: EstadoClinica }) =>
      api.patch(`/api/super-admin/clinicas/${id}/estado`, { estadoClinica }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sa-clinicas'] });
      qc.invalidateQueries({ queryKey: ['sa-stats'] });
      toast.success('Estado actualizado');
      setConfirmDelete(null);
    },
    onError: (e: any) => toast.error(getErrorMessage(e, 'Error')),
  });

  function openCreate() { setEditing(null); setForm({ ...empty }); setOpen(true); }

  function openEdit(c: any) {
    setEditing(c);
    setForm({
      nombre: c.nombre ?? '', tipoClinica: c.tipoClinica ?? 'medica_general',
      telefono: c.telefono ?? '', correo: c.correo ?? '', sitioWeb: c.sitioWeb ?? '',
      direccion: c.direccion ?? '', razonSocial: c.razonSocial ?? '', rfc: c.rfc ?? '',
      ciudad: c.ciudad ?? '', estado: c.estado ?? '', codigoNeej: c.codigoNeej ?? '',
      emailAdmin: '', nombreAdmin: '',
    });
    setOpen(true);
  }

  function closeDialog() { setOpen(false); setEditing(null); setForm({ ...empty }); }

  function field(key: keyof typeof empty) {
    return { value: form[key], onChange: (e: any) => setForm(f => ({ ...f, [key]: e.target.value })) };
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.nombre.trim()) return toast.error('El nombre es obligatorio');
    if (!editing && !form.emailAdmin.trim()) return toast.error('El email del administrador es obligatorio');
    if (!editing && !form.nombreAdmin.trim()) return toast.error('El nombre del administrador es obligatorio');
    if (editing) {
      const { emailAdmin: _e, nombreAdmin: _n, ...data } = form;
      updateMut.mutate({ id: editing.id, d: data });
    } else {
      createMut.mutate(form);
    }
  }

  const byEstado = (ec: EstadoClinica) => clinicas.filter(c => (c.estadoClinica ?? 'activa') === ec);
  const activas     = byEstado('activa');
  const suspendidas = byEstado('suspendida');
  const eliminadas  = byEstado('eliminada');

  const tabs: { value: EstadoClinica; label: string; count: number; countCls: string }[] = [
    { value: 'activa',     label: 'Activas',     count: activas.length,     countCls: 'bg-emerald-900 text-emerald-300' },
    { value: 'suspendida', label: 'Suspendidas', count: suspendidas.length, countCls: 'bg-amber-900 text-amber-300' },
    { value: 'eliminada',  label: 'Eliminadas',  count: eliminadas.length,  countCls: 'bg-red-900 text-red-400' },
  ];

  const tabData: Record<EstadoClinica, any[]> = { activa: activas, suspendida: suspendidas, eliminada: eliminadas };
  const pending = createMut.isPending || updateMut.isPending;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-white">Clínicas</h1>
          <p className="text-slate-400 text-sm mt-0.5">{clinicas.length} registradas</p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2.5 bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold text-sm rounded-xl transition-colors"
        >
          <Plus size={16} /> Nueva clínica
        </button>
      </div>

      {/* Tabs */}
      {isLoading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-slate-500" size={28} /></div>
      ) : (
        <Tabs.Root defaultValue="activa">
          <Tabs.List className="flex border-b border-slate-800 gap-1 overflow-x-auto">
            {tabs.map(t => (
              <Tabs.Trigger key={t.value} value={t.value} className={TAB_CLS}>
                {t.label}
                <span className={`ml-2 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${t.countCls}`}>
                  {t.count}
                </span>
              </Tabs.Trigger>
            ))}
          </Tabs.List>

          {tabs.map(t => (
            <Tabs.Content key={t.value} value={t.value}>
              <ClinicasTabla
                clinicas={tabData[t.value]}
                onEdit={openEdit}
                onLog={setLogClinica}
                onCicloVida={setCicloVidaClinica}
                onEstado={(id, estado) => estadoMut.mutate({ id, estadoClinica: estado })}
                onEliminar={setConfirmDelete}
                onReenviar={id => reenviarMut.mutate(id)}
                isPending={estadoMut.isPending}
              />
            </Tabs.Content>
          ))}
        </Tabs.Root>
      )}

      {/* Modal log de actividades */}
      {logClinica && <ActividadesModal clinica={logClinica} onClose={() => setLogClinica(null)} />}

      {/* Modal ciclo de vida */}
      {cicloVidaClinica && (
        <CicloVidaClinicaModal clinica={cicloVidaClinica} onClose={() => setCicloVidaClinica(null)} />
      )}

      {/* Dialog crear/editar */}
      <Dialog.Root open={open} onOpenChange={v => { if (!v) closeDialog(); }}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40" />
          <Dialog.Content className="fixed z-50 inset-x-0 bottom-0 md:inset-x-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:bottom-auto w-full md:max-w-2xl max-h-[92vh] overflow-y-auto bg-slate-900 rounded-t-3xl md:rounded-2xl border border-slate-700 shadow-2xl p-5 md:p-6">
            <div className="flex items-center justify-between mb-6">
              <Dialog.Title className="text-lg font-bold text-white">
                {editing ? 'Editar clínica' : 'Registrar nueva clínica'}
              </Dialog.Title>
              <button onClick={closeDialog} className="text-slate-400 hover:text-white transition-colors">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={submit} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="col-span-1 sm:col-span-2">
                  <label className={labelCls}>Nombre de la clínica *</label>
                  <input className={inputCls} placeholder="Ej. Clínica del Valle" required {...field('nombre')} />
                </div>
                <div>
                  <label className={labelCls}>Tipo</label>
                  <select className={`${inputCls} cursor-pointer`} {...field('tipoClinica')}>
                    {TIPO_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Código NEEJ</label>
                  <input className={inputCls} placeholder="Ej. ANIK-XXXX-XXXX" {...field('codigoNeej')} />
                </div>
                <div>
                  <label className={labelCls}>Teléfono</label>
                  <input className={inputCls} placeholder="+52 55 0000 0000" {...field('telefono')} />
                </div>
                <div>
                  <label className={labelCls}>Correo electrónico</label>
                  <input className={inputCls} type="email" placeholder="clinica@ejemplo.com" {...field('correo')} />
                </div>
                <div>
                  <label className={labelCls}>Ciudad</label>
                  <input className={inputCls} placeholder="Ciudad de México" {...field('ciudad')} />
                </div>
                <div>
                  <label className={labelCls}>Estado / Provincia</label>
                  <input className={inputCls} placeholder="CDMX" {...field('estado')} />
                </div>
                <div className="col-span-1 sm:col-span-2">
                  <label className={labelCls}>Dirección</label>
                  <input className={inputCls} placeholder="Calle, Número, Colonia" {...field('direccion')} />
                </div>
                <div>
                  <label className={labelCls}>Razón social</label>
                  <input className={inputCls} {...field('razonSocial')} />
                </div>
                <div>
                  <label className={labelCls}>RFC</label>
                  <input className={inputCls} {...field('rfc')} />
                </div>
                <div>
                  <label className={labelCls}>Sitio web</label>
                  <input className={inputCls} placeholder="https://www.clinica.com" {...field('sitioWeb')} />
                </div>
              </div>

              {!editing && (
                <div className="border-t border-slate-700 pt-5 space-y-4">
                  <p className="text-sm font-semibold text-slate-300 flex items-center gap-2">
                    <Globe size={14} className="text-amber-400" />
                    Administrador inicial
                  </p>
                  <p className="text-xs text-slate-500">Se le enviará un email de invitación para activar su cuenta.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className={labelCls}>Nombre *</label>
                      <input className={inputCls} placeholder="Ej. Dr. Carlos López" {...field('nombreAdmin')} />
                    </div>
                    <div>
                      <label className={labelCls}>Email *</label>
                      <input className={inputCls} type="email" placeholder="admin@clinica.com" {...field('emailAdmin')} />
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={closeDialog} className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors">
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={pending}
                  className="flex items-center gap-2 px-5 py-2.5 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-slate-900 font-semibold text-sm rounded-xl transition-colors"
                >
                  {pending && <Loader2 size={14} className="animate-spin" />}
                  {editing ? 'Guardar cambios' : 'Registrar y enviar invitación'}
                </button>
              </div>
            </form>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Confirm eliminar */}
      <Dialog.Root open={!!confirmDelete} onOpenChange={v => { if (!v) setConfirmDelete(null); }}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40" />
          <Dialog.Content className="fixed z-50 inset-x-0 bottom-0 md:inset-x-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:bottom-auto w-full md:max-w-sm bg-slate-900 rounded-t-3xl md:rounded-2xl border border-slate-700 shadow-2xl p-6 space-y-4">
            <div className="flex items-center gap-3 text-red-400">
              <AlertTriangle size={22} />
              <Dialog.Title className="font-bold text-white">Eliminar clínica</Dialog.Title>
            </div>
            <p className="text-sm text-slate-400">
              ¿Confirmas eliminar <strong className="text-white">{confirmDelete?.nombre}</strong>?
              El acceso de todos sus usuarios quedará bloqueado. El historial se conserva.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setConfirmDelete(null)} className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors">
                Cancelar
              </button>
              <button
                onClick={() => estadoMut.mutate({ id: confirmDelete.id, estadoClinica: 'eliminada' })}
                disabled={estadoMut.isPending}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white font-semibold text-sm rounded-xl transition-colors"
              >
                {estadoMut.isPending && <Loader2 size={13} className="animate-spin" />}
                Eliminar
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
